<template>
  <div>
    <Head :title="`Detail Prodi ${prodi}`" />

    <!-- HEADER -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold">
        Detail Program Studi: {{ prodi }}
      </h1>
      <p class="text-gray-600">Universitas {{ universitas }}</p>
    </div>

    <!-- FILTER -->
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="p-4 font-semibold bg-gray-200">Filter</div>
      <div class="flex flex-wrap w-full p-6">
        <div class="w-full font-semibold lg:w-3/12">Periode Beasiswa:</div>
        <v-select
          v-model="form.periode"
          :options="periodes"
          class="w-full lg:w-9/12"
          :reduce="p => p.id"
          label="nama"
        />
      </div>
    </div>

    <!-- PANEL TOTAL -->
    <div class="w-full p-4 mb-6 bg-white border rounded shadow">
      <h2 class="mb-2 text-lg font-semibold">Total Mahasiswa</h2>

      <p>Total: <strong>{{ total }}</strong></p>

      <div class="flex gap-3 mt-2 text-sm">
        <span class="px-3 py-1 font-semibold text-white rounded" style="background:#3b82f6">
          Laki-laki: {{ laki }}
        </span>
        <span class="px-3 py-1 font-semibold text-white rounded" style="background:#db2777">
          Perempuan: {{ perempuan }}
        </span>
      </div>
    </div>

    <!-- EXPORT BUTTON -->
    <div class="flex justify-end mb-4">
      <a
        :href="route('report.prodi.detail.export', {
          prodi: prodi,
          periode: form.periode
        })"
        class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700"
      >
        Export Excel
      </a>
    </div>

    <!-- TOMBOL KEMBALI -->
    <div class="mb-4">
      <Link
        :href="route('report.prodi', { periode: form.periode })"
        class="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 rounded border"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Kembali
      </Link>
    </div>
    <!-- TABEL -->
    <div class="w-full mb-10 bg-white border rounded shadow">
      <div class="px-4 py-3 font-semibold bg-gray-100 border-b">Daftar Mahasiswa</div>

      <div class="overflow-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b bg-gray-50">
              <!-- SORT NAMA -->
              <th class="p-2 border cursor-pointer" @click="toggleSort('nama')">
                Nama
                <span v-if="sort.field === 'nama'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <th class="p-2 border cursor-pointer" @click="toggleSort('nim')">
                NIM
                <span v-if="sort.field === 'nim'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <th class="p-2 border">JK</th>

              <th
                class="p-2 text-center border cursor-pointer"
                @click="toggleSort('semester')"
              >
                Semester
                <span v-if="sort.field === 'semester'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <th
                class="p-2 text-center border cursor-pointer"
                @click="toggleSort('penghasilan')"
              >
                Penghasilan Ortu
                <span v-if="sort.field === 'penghasilan'">
                  {{ sort.direction === 'asc' ? '↑' : '↓' }}
                </span>
              </th>

              <th class="p-2 border">Alamat</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(m, i) in sortedMahasiswa"
              :key="i"
              class="border-b hover:bg-gray-50"
            >
              <td class="p-2 border">{{ m.nama }}</td>
              <td class="p-2 border">{{ m.nim }}</td>
              <td class="p-2 border">{{ m.jk }}</td>
              <td class="p-2 text-center border">{{ m.semester }}</td>
              <td class="p-2 text-center border">{{ formatRupiah(m.penghasilan) }}</td>
              <td class="p-2 border">{{ m.alamat }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- CHART -->
    <div class="p-4 bg-white rounded shadow">
      <h2 class="mb-4 text-lg font-bold">Grafik Mahasiswa</h2>
      <apexchart
        width="100%"
        height="420"
        type="pie"
        :options="chartOptions"
        :series="[laki, perempuan]"
      />
    </div>
  </div>
</template>


<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import VueApexCharts from 'vue3-apexcharts'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

export default {
  components: { Head, Link, apexchart: VueApexCharts },
  layout: Layout,

  props: {
    prodi: String,
    universitas: String,
    mahasiswa: Array,
    total: Number,
    laki: Number,
    perempuan: Number,
    filters: Object,
    periodes: Array,
  },

  data() {
    return {
      form: { periode: parseInt(this.filters.periode) || null },

      sort: {
        field: 'nama',
        direction: 'asc',
      },
    }
  },

  computed: {
    sortedMahasiswa() {
      return [...this.mahasiswa].sort((a, b) => {
        const field = this.sort.field
        const dir = this.sort.direction === 'asc' ? 1 : -1

        let x = a[field]
        let y = b[field]

        if (['semester', 'penghasilan'].includes(field)) {
          x = Number(x)
          y = Number(y)
        }

        if (typeof x === 'string') x = x.toLowerCase()
        if (typeof y === 'string') y = y.toLowerCase()

        if (x < y) return -1 * dir
        if (x > y) return 1 * dir
        return 0
      })
    },

    chartOptions() {
      return {
        labels: ['Laki-laki', 'Perempuan'],
        colors: ['#3b82f6', '#db2777'],
        legend: { position: 'bottom' },
      }
    },
  },

  watch: {
    form: {
      handler: throttle(function () {
        const q = pickBy(this.form)
        this.$inertia.get(
          this.route('report.prodi.detail', this.prodi),
          q,
          { preserveState: true, preserveScroll: true },
        )
      }, 150),
      deep: true,
    },
  },

  methods: {
    toggleSort(field) {
      if (this.sort.field === field) {
        this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort.field = field
        this.sort.direction = 'asc'
      }
    },

    formatRupiah(v) {
      if (!v) return '-'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(v)
    },
  },
}
</script>
